/**
 * ============================================================================
 * MOCK DATA — an in-memory implementation of the `DashboardApi` interface.
 * ============================================================================
 *
 * See `src/mock/banner.ts`. This is NOT an API integration and it is not a
 * fallback path: it is a stand-in that exists because the Rust daemon does not
 * serve these routes yet. It never touches the network.
 *
 * Two deliberate behaviours, so the mock cannot be mistaken for a working
 * integration:
 *   1. every envelope is stamped `provenance: 'synthetic'` with explicit
 *      warnings, and every command receipt message is prefixed `MOCK:`;
 *   2. commands still validate the typed confirmation phrase and still mutate
 *      in-memory state, so the §3.1 safety UX can be exercised end to end —
 *      without any pretence that an order was placed.
 */
import type { DashboardApi, Fetched } from '../api/client';
import { ApiError, newIdempotencyKey } from '../api/client';
import type {
  AllowListEntryView,
  AuthSessionView,
  BacktestRunView,
  BarView,
  BridgeStatusView,
  ChartSeries,
  ClosedTradeRow,
  CommandReceipt,
  CommandRequest,
  CommandKind,
  DataHealthView,
  FeatureFreshnessRow,
  IndicatorSeriesView,
  KpiSnapshot,
  Paged,
  PositionRow,
  QuoteRowView,
  ReadinessView,
  RegimeSnapshotView,
  ResearchAssessmentView,
  ResearchCallCostSummary,
  RibbonStatus,
  RiskConfigAuditRow,
  RiskLimitRow,
  SchedulerView,
  SetAllowListPayload,
  SetExecutionModePayload,
  SetHaltPayload,
  SetRiskLimitPayload,
  SignalBreakdownView,
  SignalRow,
  StrategyEligibilityView,
  StrategyMarkerView,
  SystemEventRow,
  TerminalMetadataView,
  Timeframe,
  TradeDecisionTrailView,
  TradeFilters,
  WorkingOrderRow,
} from '../api/types';
import { CONFIRMATION_PHRASES } from '../config';
import { MOCK_DATASET, type PendingOrderIntent } from './dataset';
import {
  buildAllowLists,
  buildBacktests,
  buildBars,
  buildBridgeStatus,
  buildDataHealth,
  buildDecisionTrail,
  buildFeatureFreshness,
  buildIndicators,
  buildQuotes,
  buildReadiness,
  buildRegimes,
  buildResearchAssessments,
  buildResearchCosts,
  buildRibbon,
  buildRiskAuditLog,
  buildRiskLimits,
  buildScheduler,
  buildSignalBreakdownForDecision,
  buildStrategies,
  buildTerminalMetadata,
} from './fixtures';
import { ALL_CHART_IDS, buildChart, buildKpiSnapshot } from './metrics';
import { MOCK_PROVENANCE, MOCK_WARNINGS } from './banner';

/** Simulated latency, so loading and refresh states are exercised. */
const MIN_LATENCY_MS = 60;
const MAX_LATENCY_MS = 220;

function delay(): Promise<void> {
  const ms = MIN_LATENCY_MS + Math.random() * (MAX_LATENCY_MS - MIN_LATENCY_MS);
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function envelope<T>(data: T, asOf?: string): Fetched<T> {
  const now = new Date().toISOString();
  return {
    data,
    // `Fetched` is the unwrapped client-side shape: the wire envelope's snake_case
    // `as_of`/`server_time` fields become these camelCase properties.
    asOf: asOf ?? now,
    serverTime: now,
    provenance: MOCK_PROVENANCE,
    warnings: MOCK_WARNINGS,
  };
}

/** Mutable mock state, so the §3.1 confirmation flows visibly change something. */
interface MockState {
  execution_mode: RibbonStatus['execution_mode'];
  halt_level: RibbonStatus['halt']['level'];
  halt_reason: string | null;
  halt_activated_at: string | null;
  halt_activated_by: string | null;
  risk_limits: RiskLimitRow[];
  risk_audit: RiskConfigAuditRow[];
  allow_lists: AllowListEntryView[];
  /** Order intents awaiting a human decision (§3.1 approve/reject). */
  pending_intents: PendingOrderIntent[];
  positions: PositionRow[];
  working_orders: WorkingOrderRow[];
  /**
   * Stands in for the daemon's append-only audit ledger. The real system writes
   * these receipts to `audit_events` (§8.1); the dashboard reads them back
   * through `/risk/audit-log` and `/events/system`, not from here.
   */
  command_log: CommandReceipt[];
}

const state: MockState = {
  execution_mode: 'approval_required',
  halt_level: 'none',
  halt_reason: null,
  halt_activated_at: null,
  halt_activated_by: null,
  risk_limits: buildRiskLimits(),
  risk_audit: buildRiskAuditLog(),
  allow_lists: buildAllowLists(),
  pending_intents: [...MOCK_DATASET.pending_intents],
  positions: [...MOCK_DATASET.positions],
  working_orders: [...MOCK_DATASET.working_orders],
  command_log: [],
};

function currentRibbon(): RibbonStatus {
  const base = buildRibbon();
  const riskState: RibbonStatus['risk_state'] =
    state.halt_level === 'hard'
      ? 'hard_halted'
      : state.halt_level === 'soft'
        ? 'soft_halted'
        : state.risk_limits.some((limit) => limit.status === 'breach')
          ? 'warning'
          : 'normal';
  return {
    ...base,
    execution_mode: state.execution_mode,
    risk_state: riskState,
    halt: {
      level: state.halt_level,
      activated_at: state.halt_activated_at,
      activated_by: state.halt_activated_by,
      reason: state.halt_reason,
      source: state.halt_level === 'none' ? null : 'operator',
      // §7.3 semantics: soft halt still permits protective actions; hard halt
      // permits only the explicit emergency-close workflow.
      allows_protective_actions: state.halt_level !== 'hard',
      allows_new_entries: state.halt_level === 'none',
    },
  };
}

function filterTrades(filters: TradeFilters): ClosedTradeRow[] {
  return MOCK_DATASET.closed_trades.filter((trade) => {
    if (filters.symbols.length > 0 && !filters.symbols.includes(trade.symbol)) return false;
    if (filters.strategies.length > 0 && !filters.strategies.includes(trade.strategy_version)) return false;
    if (filters.outcomes.length > 0 && !filters.outcomes.includes(trade.outcome)) return false;
    if (filters.exit_reasons.length > 0 && !filters.exit_reasons.includes(trade.exit_reason)) return false;
    if (filters.sessions.length > 0) {
      const phase = trade.regime?.session_phase ?? 'unknown';
      if (!filters.sessions.includes(phase)) return false;
    }
    if (filters.from !== null && trade.exit_timestamp < filters.from) return false;
    if (filters.to !== null && trade.exit_timestamp > filters.to) return false;
    return true;
  });
}

const PAGE_SIZE = 25;

function commandReceipt(command: CommandKind, message: string, accepted = true, rejection: string | null = null): CommandReceipt {
  const receipt: CommandReceipt = {
    command_id: newIdempotencyKey(),
    command,
    accepted,
    // The MOCK: prefix is deliberate and load-bearing. A receipt must never be
    // mistakable for a real daemon acknowledgement.
    message: accepted ? `MOCK: ${message}` : `MOCK (rejected): ${message}`,
    received_at: new Date().toISOString(),
    audit_event_id: accepted ? newIdempotencyKey() : null,
    resulting_halt: state.halt_level,
    resulting_execution_mode: state.execution_mode,
    rejection_reason: rejection,
  };
  state.command_log.push(receipt);
  return receipt;
}

function recordRiskAudit(target: string, prior: string, next: string, reason: string): RiskConfigAuditRow {
  const row: RiskConfigAuditRow = {
    audit_event_id: newIdempotencyKey(),
    occurred_at: new Date().toISOString(),
    operator: 'operator.ayoub',
    target,
    target_kind: 'risk_limit',
    prior_value: prior,
    new_value: next,
    reason,
    config_version_before: 'risk-2026.09.4',
    config_version_after: 'risk-2026.09.5',
    replay_validation: { required: true, performed: false, session_date: null, result: 'not_run' },
    chain_hash: `sha256:mock-${Date.now().toString(16)}`,
    previous_chain_hash: state.risk_audit[0]?.chain_hash ?? null,
    source_ip: '127.0.0.1',
  };
  state.risk_audit = [row, ...state.risk_audit];
  return row;
}

function expectedPhrase(command: CommandKind, payload: unknown): string {
  if (command === 'set_halt') {
    const level = (payload as SetHaltPayload).level;
    if (level === 'soft') return CONFIRMATION_PHRASES.soft_halt;
    if (level === 'hard') return CONFIRMATION_PHRASES.hard_halt;
    return CONFIRMATION_PHRASES.release_halt;
  }
  return CONFIRMATION_PHRASES[command];
}

export function createMockApi(): DashboardApi {
  return {
    mode: 'mock',

    getSession: async (): Promise<Fetched<AuthSessionView>> => {
      await delay();
      return envelope<AuthSessionView>({
        authenticated: true,
        operator: 'operator.ayoub',
        // An obviously fake token. The real token comes from the daemon and is
        // never persisted outside memory.
        csrf_token: 'mock-csrf-token-not-a-credential',
        issued_at: new Date(MOCK_DATASET.now - 3 * 3_600_000).toISOString(),
        expires_at: new Date(MOCK_DATASET.now + 5 * 3_600_000).toISOString(),
        role: 'operator',
      });
    },

    login: async (operator: string): Promise<Fetched<AuthSessionView>> => {
      await delay();
      if (operator.trim().length === 0) {
        throw new ApiError(422, { code: 'operator_required', message: 'An operator name is required.' }, 'operator required');
      }
      return envelope<AuthSessionView>({
        authenticated: true,
        operator: operator.trim(),
        csrf_token: 'mock-csrf-token-not-a-credential',
        issued_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 8 * 3_600_000).toISOString(),
        role: 'operator',
      });
    },

    logout: async (): Promise<Fetched<{ ok: boolean }>> => {
      await delay();
      return envelope({ ok: true });
    },

    getRibbon: async (): Promise<Fetched<RibbonStatus>> => {
      await delay();
      const ribbon = currentRibbon();
      return envelope(ribbon, ribbon.data_freshness.last_tick_at ?? ribbon.as_of);
    },

    getKpis: async (): Promise<Fetched<KpiSnapshot>> => {
      await delay();
      const snapshot = buildKpiSnapshot({ ...MOCK_DATASET, positions: state.positions });
      return envelope(snapshot, snapshot.as_of);
    },

    getPositions: async (): Promise<Fetched<PositionRow[]>> => {
      await delay();
      return envelope([...state.positions]);
    },

    getWorkingOrders: async (): Promise<Fetched<WorkingOrderRow[]>> => {
      await delay();
      return envelope([...state.working_orders]);
    },

    getClosedTrades: async (filters: TradeFilters, cursor?: string): Promise<Fetched<Paged<ClosedTradeRow>>> => {
      await delay();
      const filtered = filterTrades(filters).sort((left, right) => right.exit_timestamp.localeCompare(left.exit_timestamp));
      const offset = cursor === undefined ? 0 : Number.parseInt(cursor, 10) || 0;
      const items = filtered.slice(offset, offset + PAGE_SIZE);
      const nextOffset = offset + PAGE_SIZE;
      return envelope<Paged<ClosedTradeRow>>({
        items,
        total: filtered.length,
        next_cursor: nextOffset < filtered.length ? String(nextOffset) : null,
      });
    },

    getSignals: async (): Promise<Fetched<SignalRow[]>> => {
      await delay();
      return envelope([...MOCK_DATASET.signals]);
    },

    getSystemEvents: async (since?: string): Promise<Fetched<SystemEventRow[]>> => {
      await delay();
      const events = since === undefined ? MOCK_DATASET.system_events : MOCK_DATASET.system_events.filter((event) => event.occurred_at > since);
      return envelope(events);
    },

    getDecisionTrail: async (completedTradeId: string): Promise<Fetched<TradeDecisionTrailView>> => {
      await delay();
      const trail = buildDecisionTrail(completedTradeId);
      if (trail === null) {
        throw new ApiError(
          404,
          { code: 'trade_not_found', message: `No completed trade with id ${completedTradeId} exists in the mock ledger.`, retryable: false },
          'trade not found',
        );
      }
      return envelope(trail);
    },

    getChart: async (chartId): Promise<Fetched<ChartSeries>> => {
      await delay();
      if (!ALL_CHART_IDS.includes(chartId)) {
        throw new ApiError(404, { code: 'chart_not_found', message: `Unknown chart ${chartId}.`, retryable: false }, 'chart not found');
      }
      // Filters are accepted for contract parity; the mock charts render the
      // whole synthetic ledger rather than pretending to filter it.
      return envelope(buildChart(MOCK_DATASET, chartId));
    },

    getQuotes: async (symbols?: string[]): Promise<Fetched<QuoteRowView[]>> => {
      await delay();
      const quotes = buildQuotes();
      return envelope(symbols === undefined || symbols.length === 0 ? quotes : quotes.filter((quote) => symbols.includes(quote.symbol)));
    },

    getBars: async (symbol: string, timeframe: Timeframe, limit: number): Promise<Fetched<BarView[]>> => {
      await delay();
      const bars = buildBars(symbol, timeframe, Math.min(Math.max(limit, 10), 500));
      return envelope(bars, bars[bars.length - 1]?.time);
    },

    getIndicators: async (symbol: string, timeframe: Timeframe): Promise<Fetched<IndicatorSeriesView[]>> => {
      await delay();
      return envelope(buildIndicators(symbol, timeframe));
    },

    getMarkers: async (symbol: string): Promise<Fetched<StrategyMarkerView[]>> => {
      await delay();
      return envelope(MOCK_DATASET.markers.filter((marker) => marker.symbol === symbol));
    },

    getSignalBreakdown: async (decisionId: string): Promise<Fetched<SignalBreakdownView>> => {
      await delay();
      const breakdown = buildSignalBreakdownForDecision(decisionId);
      if (breakdown === null) {
        throw new ApiError(
          404,
          { code: 'signal_not_found', message: `No signal candidate ${decisionId} exists in the mock ledger.`, retryable: false },
          'signal not found',
        );
      }
      return envelope(breakdown);
    },

    getRiskLimits: async (): Promise<Fetched<RiskLimitRow[]>> => {
      await delay();
      return envelope([...state.risk_limits]);
    },

    getRiskAuditLog: async (): Promise<Fetched<RiskConfigAuditRow[]>> => {
      await delay();
      return envelope([...state.risk_audit]);
    },

    getAllowLists: async (): Promise<Fetched<AllowListEntryView[]>> => {
      await delay();
      return envelope([...state.allow_lists]);
    },

    getStrategies: async (): Promise<Fetched<StrategyEligibilityView[]>> => {
      await delay();
      return envelope(buildStrategies());
    },

    getResearchAssessments: async (limit?: number): Promise<Fetched<ResearchAssessmentView[]>> => {
      await delay();
      const assessments = buildResearchAssessments();
      return envelope(limit === undefined ? assessments : assessments.slice(0, limit));
    },

    getRegimes: async (symbol?: string): Promise<Fetched<RegimeSnapshotView[]>> => {
      await delay();
      const regimes = buildRegimes();
      return envelope(symbol === undefined ? regimes : regimes.filter((regime) => regime.symbol === symbol));
    },

    getFeatureFreshness: async (): Promise<Fetched<FeatureFreshnessRow[]>> => {
      await delay();
      return envelope(buildFeatureFreshness());
    },

    getResearchCosts: async (): Promise<Fetched<ResearchCallCostSummary>> => {
      await delay();
      return envelope(buildResearchCosts());
    },

    getBacktests: async (): Promise<Fetched<BacktestRunView[]>> => {
      await delay();
      return envelope(buildBacktests());
    },

    getBacktest: async (runId: string): Promise<Fetched<BacktestRunView>> => {
      await delay();
      const run = buildBacktests().find((candidate) => candidate.run_id === runId);
      if (run === undefined) {
        throw new ApiError(404, { code: 'backtest_not_found', message: `Unknown backtest run ${runId}.`, retryable: false }, 'backtest not found');
      }
      return envelope(run);
    },

    getReadiness: async (): Promise<Fetched<ReadinessView>> => {
      await delay();
      const readiness = buildReadiness();
      return envelope({ ...readiness, as_of: new Date().toISOString() });
    },

    getBridgeStatus: async (): Promise<Fetched<BridgeStatusView>> => {
      await delay();
      return envelope(buildBridgeStatus());
    },

    getTerminalMetadata: async (): Promise<Fetched<TerminalMetadataView>> => {
      await delay();
      return envelope(buildTerminalMetadata());
    },

    getScheduler: async (): Promise<Fetched<SchedulerView>> => {
      await delay();
      return envelope(buildScheduler());
    },

    getDataHealth: async (): Promise<Fetched<DataHealthView>> => {
      await delay();
      return envelope(buildDataHealth());
    },

    /**
     * Commands in mock mode mutate in-memory state only. The confirmation phrase
     * is still verified, mirroring the check the daemon must perform, so the UI
     * cannot be "tested" into believing a dialog is sufficient.
     */
    sendCommand: async <TPayload>(request: CommandRequest<TPayload>): Promise<Fetched<CommandReceipt>> => {
      await delay();
      const expected = expectedPhrase(request.command, request.payload);
      if (request.confirmation.trim() !== expected) {
        throw new ApiError(
          422,
          {
            code: 'confirmation_mismatch',
            message: `Confirmation phrase did not match. Expected "${expected}".`,
            fields: [{ field: 'confirmation', message: 'Typed confirmation did not match the required phrase.' }],
            retryable: false,
          },
          'confirmation mismatch',
        );
      }

      switch (request.command) {
        case 'set_execution_mode': {
          const payload = request.payload as SetExecutionModePayload;
          const prior = state.execution_mode;
          state.execution_mode = payload.mode;
          return envelope(commandReceipt(request.command, `execution mode changed from ${prior} to ${payload.mode}.`));
        }
        case 'set_halt': {
          const payload = request.payload as SetHaltPayload;
          const prior = state.halt_level;
          state.halt_level = payload.level;
          state.halt_reason = payload.level === 'none' ? null : payload.reason;
          state.halt_activated_at = payload.level === 'none' ? null : new Date().toISOString();
          state.halt_activated_by = payload.level === 'none' ? null : 'operator.ayoub';
          return envelope(
            commandReceipt(request.command, `halt changed from ${prior} to ${payload.level}. New entries ${payload.level === 'none' ? 'permitted' : 'blocked'}.`),
          );
        }
        case 'approve_order_intent': {
          const decisionId = (request.payload as { decision_id: string }).decision_id;
          const target = state.pending_intents.find((intent) => intent.decision_id === decisionId);
          state.pending_intents = state.pending_intents.filter((intent) => intent.decision_id !== decisionId);
          return envelope(
            commandReceipt(
              request.command,
              target === undefined
                ? 'order intent approved (intent was no longer queued).'
                : `order intent for ${target.symbol} approved by the operator. No MT5 order was sent: this build has no execution path.`,
            ),
          );
        }
        case 'reject_order_intent': {
          const decisionId = (request.payload as { decision_id: string }).decision_id;
          state.pending_intents = state.pending_intents.filter((intent) => intent.decision_id !== decisionId);
          return envelope(commandReceipt(request.command, 'order intent rejected and recorded in the audit ledger.'));
        }
        case 'cancel_order': {
          const ticket = (request.payload as { order_ticket: string }).order_ticket;
          const target = state.working_orders.find((order) => order.order_ticket === ticket);
          if (target === undefined) {
            return envelope(commandReceipt(request.command, `no working order ${ticket}.`, false, 'order_not_found'));
          }
          if (!target.cancellable) {
            return envelope(
              commandReceipt(request.command, `order ${ticket} cannot be cancelled: ${target.cancel_blocked_reason ?? 'not cancellable'}.`, false, 'order_not_cancellable'),
            );
          }
          state.working_orders = state.working_orders.filter((order) => order.order_ticket !== ticket);
          return envelope(commandReceipt(request.command, `cancellation acknowledged for ${target.symbol} order ${ticket}.`));
        }
        case 'close_position': {
          const positionTicket = (request.payload as { position_ticket: string }).position_ticket;
          const target = state.positions.find((position) => position.position_ticket === positionTicket);
          if (target === undefined) {
            return envelope(commandReceipt(request.command, `no open position ${positionTicket}.`, false, 'position_not_found'));
          }
          state.positions = state.positions.filter((position) => position.position_ticket !== positionTicket);
          return envelope(
            commandReceipt(
              request.command,
              `close requested for ${target.symbol} position ${positionTicket} (${target.lots} lots). No MT5 request was sent: this build has no execution path.`,
            ),
          );
        }
        case 'set_risk_limit': {
          const payload = request.payload as SetRiskLimitPayload;
          const target = state.risk_limits.find((limit) => limit.limit_id === payload.limit_id);
          if (target === undefined) {
            return envelope(commandReceipt(request.command, `unknown risk limit ${payload.limit_id}.`, false, 'limit_not_found'));
          }
          if (!target.editable) {
            return envelope(commandReceipt(request.command, `${target.label} is not editable from the dashboard.`, false, 'limit_not_editable'));
          }
          const priorDisplay = target.configured_display;
          state.risk_limits = state.risk_limits.map((limit) =>
            limit.limit_id === payload.limit_id
              ? {
                  ...limit,
                  configured_value: payload.new_value,
                  configured_display: String(payload.new_value),
                  config_version: 'risk-2026.09.5',
                  last_modified_by: 'operator.ayoub',
                  last_modified_at: new Date().toISOString(),
                }
              : limit,
          );
          recordRiskAudit(target.label, priorDisplay, String(payload.new_value), payload.reason);
          return envelope(
            commandReceipt(
              request.command,
              `${target.label} changed from "${priorDisplay}" to "${payload.new_value}". Reason and prior value recorded in the audit log. Replay validation is NOT run in mock mode.`,
            ),
          );
        }
        case 'set_allow_list': {
          const payload = request.payload as SetAllowListPayload;
          state.allow_lists = state.allow_lists.map((entry) =>
            entry.entry_id === payload.entry_id
              ? { ...entry, enabled: payload.enabled, last_modified_by: 'operator.ayoub', last_modified_at: new Date().toISOString() }
              : entry,
          );
          return envelope(commandReceipt(request.command, `allow-list entry ${payload.value} set to ${payload.enabled ? 'enabled' : 'disabled'}.`));
        }
        default: {
          const exhaustive: never = request.command;
          throw new ApiError(400, { code: 'unknown_command', message: `Unknown command ${String(exhaustive)}.`, retryable: false }, 'unknown command');
        }
      }
    },
  };
}
